/**
 * AccountStatusNotice — twee serverzijdige statussen die nergens werden getoond.
 *
 * WAAROM DIT ER IS
 *
 * `admin_list_accounts` (0025) stuurt `teacher_status` (0020) en `consent_status`
 * (0005) al mee, en `DirectoryAccount` neemt ze al over. Geen enkel adminscherm
 * deed er iets mee. Het SSOT-register had dit vastgelegd als "niet-aangesloten
 * bron" — geen drift, maar een bron die bestaat en niet gelezen wordt.
 *
 * Dat is duurder dan het klinkt, want dit zijn precies de twee statussen waarop
 * een beheerder moet HANDELEN:
 *
 *   * een leerkracht met `pending` wacht op iemand. Zolang niemand hem ziet,
 *     wacht hij eeuwig — en `is_teacher_of()` (0020) houdt hem ondertussen
 *     buiten élk leerlingpad. Het account werkt dus half, en de leerkracht kan
 *     zelf niet zien waarom.
 *   * een leerling met ingetrokken toestemming mag niet meer verwerkt worden.
 *     Dat is een AVG-verplichting, geen voorkeur.
 *
 * WAT DIT BEWUST NIET DOET
 *
 * Geen regel bij "alles in orde". Een melding per account die in 95% van de
 * gevallen "geen bijzonderheden" zegt, wordt na een dag niet meer gelezen — en
 * dan mist de beheerder juist de 5% die telt. Zwijgen is hier informatie.
 *
 * Ook geen goedkeurknop. `verify_teacher()` bestaat (0020), maar verifiëren is
 * een eigen handeling met een eigen afweging (klopt deze school, klopt deze
 * persoon) en hoort niet als losse knop in een detailpaneel dat over iets
 * anders gaat. Deze melding wijst het probleem aan; het afhandelen komt apart.
 *
 * Dat "apart" bestaat inmiddels: `TeacherVerificationPanel` staat er direct
 * onder. Deze melding zegt wat er AAN DE HAND is, dat paneel is de HANDELING —
 * en die scheiding blijft, want een knop naast een waarschuwing nodigt uit tot
 * het wegklikken van de waarschuwing in plaats van tot controleren.
 */

import React from 'react';
import { ShieldAlert, ShieldX, UserX, Info } from 'lucide-react';
import type { DirectoryAccount } from '../../../../data/supabase/adminDirectory';
interface AccountStatusNoticeProps {
  /** De serverrij voor dit account, of undefined als het alleen lokaal bestaat. */
  account: DirectoryAccount | undefined;
}
interface Notice {
  icon: typeof ShieldAlert;
  title: string;
  body: string;
  tone: 'amber' | 'rose' | 'slate';
}
const TONE: Record<Notice['tone'], string> = {
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  rose: 'border-rose-200 bg-rose-50 text-rose-900',
  slate: 'border-slate-200 bg-slate-50 text-slate-700'
};
const ICON_TONE: Record<Notice['tone'], string> = {
  amber: 'text-amber-600',
  rose: 'text-rose-600',
  slate: 'text-slate-500'
};
function buildNotices(account: DirectoryAccount): Notice[] {
  const notices: Notice[] = [];
  if (account.role === 'teacher') {
    if (account.teacherStatus === 'pending') {
      notices.push({
        icon: ShieldAlert,
        tone: 'amber',
        title: 'Wacht op verificatie',
        body: 'Deze leerkracht kan inloggen, maar komt bij geen enkele leerling — ook niet in een klas waarvan hij eigenaar is. Zolang niemand hem verifieert, blijft dat zo, en hij ziet zelf niet waaróm het niet werkt.'
      });
    }
    if (account.teacherStatus === 'rejected') {
      notices.push({
        icon: ShieldX,
        tone: 'rose',
        title: 'Verificatie afgewezen',
        body: 'Dit account is afgewezen en heeft geen toegang tot leerlinggegevens. Het bestaat nog wel; opheffen is een aparte handeling.'
      });
    }
  }
  if (account.role === 'learner' && account.consentStatus && account.consentStatus !== 'granted') {
    notices.push({
      icon: UserX,
      tone: 'rose',
      title: 'Toestemming ingetrokken',
      body: 'De ouder heeft de toestemming voor dit kind ingetrokken. Nieuwe gegevens worden niet meer bewaard. Ga hier niet zelf mee aan de slag zonder de ouder — intrekken is hun beslissing, niet die van beheer.'
    });
  }

  // De verwerkingsverantwoordelijke is geen waarschuwing maar wel bepalend: hij
  // zegt WIE over dit kind beslist. Bij 'school' loopt een verwijderverzoek via
  // de school en niet via de ouder, en dat is precies het soort verwarring dat
  // een support-gesprek laat ontsporen.
  if (account.role === 'learner' && account.dataController) {
    notices.push({
      icon: Info,
      tone: 'slate',
      title: account.dataController === 'school' ? 'De school beslist over dit account' : 'De ouder beslist over dit account',
      body: account.dataController === 'school' ? 'Verzoeken om inzage of verwijdering lopen via de school, niet via een ouder.' : 'Verzoeken om inzage of verwijdering lopen via de gekoppelde ouder(s).'
    });
  }
  return notices;
}
export function AccountStatusNotice({
  account
}: AccountStatusNoticeProps) {
  if (!account) return null;
  const notices = buildNotices(account);
  if (notices.length === 0) return null;
  return <div className="mb-4 space-y-2" data-id="element-7746" data-mp="25271g6kq1r79">
      {notices.map(notice => {
      const Icon = notice.icon;
      return <div key={notice.title} className={`flex gap-3 rounded-xl border p-3 ${TONE[notice.tone]}`} data-id="element-7747" data-mp="292hf85er9lwo">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ICON_TONE[notice.tone]}`} aria-hidden="true" data-id="element-7748" data-mp="21jzut45ccmox" />
            <div className="min-w-0" data-id="element-7749" data-mp="1wpvis6h7xogz">
              <p className="text-sm font-bold" data-id="element-7750" data-mp="1rsdit5l6ijl4">{notice.title}</p>
              <p className="mt-0.5 text-sm leading-relaxed opacity-90" data-id="element-7751" data-mp="1rqslj87hcmjk">
                {notice.body}
              </p>
            </div>
          </div>;
    })}
    </div>;
}